import { useCallback } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { DateTime } from 'luxon';
import { FormProvider, useForm } from 'react-hook-form';

import { useAppDispatch } from 'src/app/store/hooks';
import { DriverSelectionField } from 'src/common/DriverSelectionField';
import {
  AssignmentRead,
  DispatchTripRead,
  useAssignmentsTransferCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { FormFieldController } from 'src/common/FormField/FormFieldController';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import { formatFullName } from 'src/common/util/formatFullName';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { AttendantSelectionField } from 'src/features/team/assignment-management/MultiAssignmentManagementForm/AttendantSelectionField';
import { VehicleSelectionField } from 'src/features/team/assignment-management/MultiAssignmentManagementForm/VehicleSelectionField';

import {
  setShowAssignmentManagementModal,
  setShowAssignmentTransferModal,
} from '../../assignmentManagement.slice';
import { getDefaultSplitDatetime } from './getDefaultSplitDatetime';

interface TransferFormValues {
  split_datetime: string;
  vehicle: SelectOption<string> | undefined;
  driver: SelectOption<string> | undefined;
  attendants: SelectOption<string>[] | undefined;
}

export interface AssignmentTransferFormProps {
  assignment: AssignmentRead;
  assignedTrips: DispatchTripRead[];
  onClose: () => void;
}

export function AssignmentTransferForm({
  assignment,
  assignedTrips,
  onClose,
}: AssignmentTransferFormProps) {
  const dispatch = useAppDispatch();
  const [transferAssignment] = useAssignmentsTransferCreateMutation();

  const defaultSplitDatetime = getDefaultSplitDatetime(
    assignedTrips,
    assignment.id
  );

  const methods = useForm<TransferFormValues>({
    defaultValues: {
      split_datetime: defaultSplitDatetime,
      driver: assignment.driver.driver
        ? {
            value: assignment.driver.driver,
            label: formatFullName(assignment.driver),
          }
        : undefined,
      attendants: assignment.attendants.map((at) => {
        return {
          value: at.attendant!,
          label: formatFullName(at),
        };
      }),
    },
  });

  const filterVehicles = useCallback(
    (vehicles: SelectOption<string>[]) => {
      return vehicles.filter((vehicleOption) => {
        const assignedVehicle = assignment.vehicle.vehicle;
        return vehicleOption.value !== assignedVehicle;
      });
    },
    [assignment]
  );

  const onSubmit = async (values: TransferFormValues) => {
    try {
      await transferAssignment({
        id: assignment.id,
        assignmentTransfer: {
          split_datetime: DateTime.fromFormat(
            values.split_datetime,
            "yyyy-MM-dd'T'HH:mm"
          ).toISO() as string,
          vehicle: { vehicle: values.vehicle!.value },
          driver: { driver: values.driver!.value },
          attendants:
            values.attendants?.map((option) => ({ attendant: option.value })) ??
            [],
        },
      }).unwrap();

      dispatch(
        show({
          title: 'Vehicle transferred',
          description:
            'The crew schedule has been split and the new vehicle assigned.',
          type: 'success',
        })
      );

      dispatch(setShowAssignmentTransferModal(false));
      dispatch(setShowAssignmentManagementModal(false));
    } catch (error) {
      dispatch(
        show({
          title: 'Transfer failed',
          description: formatServerError(error),
          type: 'error',
        })
      );
    }
  };

  return (
    <Modal
      open
      setOpen={(isOpen) => {
        if (!isOpen) onClose();
      }}
      onInteractOutside={(e) => e.preventDefault()}
      title="Transfer Vehicle"
    >
      <FormProvider {...methods}>
        <form className="flex flex-col gap-4 p-4">
          <FormFieldController
            name="split_datetime"
            rules={{ required: 'Transfer time is required' }}
          >
            {({ field, fieldState }) => (
              <DefaultFormFieldLayout
                label="Transfer time"
                error={fieldState.error?.message?.toString()}
                inputId="split_datetime"
              >
                <input
                  id="split_datetime"
                  type="datetime-local"
                  className="focus:border-mint-500 focus:ring-mint-500 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1"
                  {...field}
                />
              </DefaultFormFieldLayout>
            )}
          </FormFieldController>

          <VehicleSelectionField
            onlySchedulable
            filterVehicles={filterVehicles}
          />
          <DriverSelectionField onlySchedulable />
          <AttendantSelectionField onlySchedulable />

          <div className="mt-2 flex flex-row justify-between">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={methods.handleSubmit(onSubmit)}>
              Transfer
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}
